import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionLink } from '../components/ActionControls';
import { useSalon } from '../store/slice/salonSlice';

function formatDate(value) {
  if (!value) return '—';

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

function VendorsPage() {
  const [filters, setFilters] = useState({
    search: '',
    sortBy: '',
  });

  const {
    pendingApprovalSalons,
    pendingApprovalLoading,
    loading: salonActionLoading,
    error: salonError,
    fetchPendingApprovalSalons,
    updateSalonApproval,
  } = useSalon();

  useEffect(() => {
    fetchPendingApprovalSalons();
  }, [fetchPendingApprovalSalons]);

  useEffect(() => {
    if (salonError) toast.error(salonError);
  }, [salonError]);

  const handleFilterChange = (key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
  };

  const handleUpdateSalonApproval = async (id, name, approved) => {
    if (approved === 'rejected' && !window.confirm(`Are you sure you want to reject "${name}"?`)) {
      return;
    }

    const result = await updateSalonApproval({ salonId: id, approved });
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(
        approved === 'approved'
          ? `Approved "${name}" successfully`
          : `Rejected "${name}" successfully`
      );
      fetchPendingApprovalSalons();
    }
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'name-asc', label: 'Name (A-Z)' },
        { value: 'name-desc', label: 'Name (Z-A)' },
        { value: 'created_at-desc', label: 'Newest first' },
        { value: 'created_at-asc', label: 'Oldest first' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredSalonRequests = useMemo(() => {
    let result = [...(pendingApprovalSalons || [])];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (s) =>
          (s.name || '').toLowerCase().includes(term) ||
          (s.city || '').toLowerCase().includes(term) ||
          (s.address_line_1 || '').toLowerCase().includes(term) ||
          (s.email || '').toLowerCase().includes(term) ||
          (s.phone || '').includes(term)
      );
    }
    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va = (a[field] ?? '').toString().toLowerCase();
        let vb = (b[field] ?? '').toString().toLowerCase();
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [pendingApprovalSalons, filters]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Vendor Requests</h1>
          <p className="mt-1 text-slate-500">Review and approve salon requests submitted by vendors</p>
        </div>
        <div className="inline-flex items-center rounded-full bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
          {pendingApprovalSalons?.length ?? 0} pending request
          {(pendingApprovalSalons?.length ?? 0) === 1 ? '' : 's'}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Pending Salons</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{pendingApprovalSalons?.length ?? 0}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredSalonRequests.length}</p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search salon requests by name, city, or address..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        {pendingApprovalLoading ? (
          <div className="py-16 text-center text-slate-500">Loading salon requests...</div>
        ) : filteredSalonRequests.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            {pendingApprovalSalons?.length === 0
              ? 'No salon requests are waiting for approval.'
              : 'No salon requests found matching your criteria.'}
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80">
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Salon</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">City</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Submitted</th>
                  <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSalonRequests.map((salon) => (
                  <tr key={salon.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-slate-800">{salon.name || `Salon #${salon.id}`}</p>
                        <p className="text-sm text-slate-500">{salon.address_line_1 || 'Address unavailable'}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-slate-600 text-sm">{salon.city || '—'}</td>
                    <td className="py-4 px-6 text-slate-600 text-sm">{salon.email || salon.phone || '—'}</td>
                    <td className="py-4 px-6 text-slate-600 text-sm">{formatDate(salon.created_at)}</td>
                    <td className="py-4 px-6">
                      <div className="flex flex-wrap justify-end gap-2">
                        <ActionLink to={`/salons/${salon.id}`} variant="neutral">
                          View
                        </ActionLink>
                        <ActionButton
                          type="button"
                          variant="success"
                          onClick={() =>
                            handleUpdateSalonApproval(
                              salon.id,
                              salon.name || `Salon #${salon.id}`,
                              'approved'
                            )
                          }
                          disabled={salonActionLoading}
                        >
                          Approve
                        </ActionButton>
                        <ActionButton
                          type="button"
                          variant="danger"
                          onClick={() =>
                            handleUpdateSalonApproval(
                              salon.id,
                              salon.name || `Salon #${salon.id}`,
                              'rejected'
                            )
                          }
                          disabled={salonActionLoading}
                        >
                          Reject
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorsPage;
